/**
 * SEO Variants Separator
 * Разделяет пикеры вариантов (Metal, Carat, Type, Size, Color, Clarity) по отдельным вкладкам.
 * Поддерживает:
 * 1. King Linked Options (king-linked-options-product, .lv-option-box, #lv-option-product-group-data)
 * 2. Native Shopify (div.option-selector с legend.label + input.js-option)
 */

(function() {
  'use strict';

  // Флаг: успешно перенесли пикеры ИЛИ корректно завершили попытки (чтобы не крутить интервалы бесконечно)
  let separationComplete = false;
  let sizeBuildAttempts = 0;
  /** Один активный цикл retry: иначе MutationObserver + повторные вызовы init создают десятки setInterval и вешают страницу */
  let separationIntervalId = null;
  let observerDebounceTimer = null;
  /** Сколько раз observer сбрасывал separationComplete для позднего King — без лимита возможен вечный цикл при ре-рендере приложения */
  let lateSeparationObserverRetries = 0;
  const MAX_LATE_SEPARATION_OBSERVER_RETRIES = 6;

  /**
   * Инъекция CSS стилей для вариантов в табах
   * King Linked Options (.lv-option-box) + Native (.option-selector)
   */
  function injectStyles() {
    if (document.getElementById('variants-separator-styles')) return;

    const styles = document.createElement('style');
    styles.id = 'variants-separator-styles';
    styles.textContent = `
      /* Скрыть legends для нативных вариантов в таргетах */
      #seo-variants-color-target .option-selector legend,
      #seo-variants-clarity-target .option-selector legend,
      #seo-variants-size-target .option-selector legend,
      #seo-variants-diamond-type-target .option-selector legend,
      #seo-variants-main-carat-target .option-selector legend,
      #seo-variants-backings-target .option-selector legend,
      #seo-variants-purity-target .option-selector legend,
      #seo-variants-metal-target .option-selector legend {
        display: none !important;
      }
      .option-selector__btns input[type="radio"] {
        display: none !important;
      }
      .variant-info-block {
        margin-top: 20px;
        padding-top: 0;
        border-top: none;
      }
      .variant-info-block__icon { width: 80px; height: 80px; }
      .variant-info-block__icon img { width: 80px; height: 80px; object-fit: contain; }
      .variant-info-block__description { font-size: 14px; line-height: 1.6; color: #333; }
      .variant-info-block__description p { margin: 0; }
    `;
    document.head.appendChild(styles);
  }

  /**
   * Нормализует название опции в формат ID (как в Liquid)
   * @param {string} optionName - Название опции (например, "Diamond Type")
   * @returns {string} - Нормализованное название (например, "diamond-type")
   */
  function normalizeOptionName(optionName) {
    return optionName
      .toLowerCase()
      .trim()
      .replace(/\s+/g, '-')  // Пробелы → дефисы
      .replace(/_/g, '-');   // Подчеркивания → дефисы
  }

  /**
   * Генерирует селектор целевого контейнера для опции
   * @param {string} optionName - Название опции
   * @returns {string} - CSS селектор (например, "#seo-variants-metal-target")
   */
  function getTargetSelector(optionName) {
    const normalizedName = normalizeOptionName(optionName);
    return `#seo-variants-${normalizedName}-target`;
  }

  /**
   * Маппинг названия опции (King Linked Options / иврит или EN) в имя таба
   * @param {string} optionName - Текст из data-option-name или .lv-option-label__name
   * @returns {string|null} - 'metal' | 'main-carat' | 'diamond-type' | 'size' | 'color' | 'clarity' | null
   */
  function getTargetTabNameForOption(optionName) {
    if (!optionName || typeof optionName !== 'string') return null;
    const name = optionName.trim();
    const lower = name.toLowerCase();
    if (lower.includes('metal') || name.includes('מתכת')) return 'metal';
    if (lower.includes('carat') || name.includes('קראט')) return 'main-carat';
    if (lower.includes('type') || name.includes('יהלום') || name.includes('יהלומים') || name.includes('סוג יהלום') || name.includes('סוג היהלום')) return 'diamond-type';
    if (lower.includes('size') || name.includes('מידה')) return 'size';
    if (lower.includes('color') || name.includes('צבע')) return 'color';
    if (lower.includes('clarity') || name.includes('בהירות') || name.includes('ניקיון')) return 'clarity';
    if (lower.includes('purity') || name.includes('טוהר')) return 'purity';
    if (lower.includes('backing') || lower.includes('backings') || name.includes('גיבויים') || name.includes('גיבוי') || name.includes('סוגרים')) {
      return 'backings';
    }
    return null;
  }

  /**
   * Блок product options tabs на основной странице товара (не quickbuy drawer), иначе querySelector('#...-target')
   * попадает в первый дубликат id и клоны King оказываются в скрытом блоке — в табе «пусто».
   */
  function getMainProductOptionsTabsRoot() {
    const nodes = document.querySelectorAll('[data-product-options-tabs]');
    for (let i = 0; i < nodes.length; i++) {
      const el = nodes[i];
      if (!el.closest('.quickbuy-container')) return el;
    }
    return nodes.length ? nodes[0] : null;
  }

  /**
   * Контейнер #seo-variants-{tab}-target внутри основного блока табов.
   */
  function getSeoTargetContainer(targetTabName) {
    const id = 'seo-variants-' + targetTabName + '-target';
    const tabRoot = getMainProductOptionsTabsRoot();
    if (tabRoot) {
      const scoped = tabRoot.querySelector('#' + id);
      if (scoped) return scoped;
    }
    return document.getElementById(id);
  }

  /**
   * Форма товара на PDP (не в quickbuy), для .option-selectors
   */
  function getMainProductForm() {
    const forms = document.querySelectorAll('.product-form[data-product-id]');
    for (let i = 0; i < forms.length; i++) {
      if (!forms[i].closest('.quickbuy-container')) return forms[i];
    }
    const fallback = document.querySelector('.product-form[data-product-id]');
    return fallback || document.querySelector('.product-form');
  }

  /**
   * Есть ли в DOM King-опции, которые ещё не перенесены и для которых в разметке есть целевой #seo-variants-*-target.
   * Без этого MutationObserver при отсутствии мета/таргетов в Liquid будет бесконечно триггерить повторные init.
   */
  function needsLateKingSeparation() {
    const kingRoot = document.querySelector('king-linked-options-product, [id*="linkedoption"] king-linked-options-product');
    const multiBox = document.querySelector('king-linked-options-product .lv-option-multi-box, [id*="linkedoption"] .lv-option-multi-box, .lv-option-multi-box');
    const sourceRoot = multiBox || kingRoot;
    if (!sourceRoot) return false;

    const boxes = sourceRoot.querySelectorAll('.lv-option-box:not([data-seo-separated="true"])');
    for (let i = 0; i < boxes.length; i++) {
      const box = boxes[i];
      const optionName = box.getAttribute('data-option-name') || box.querySelector('.lv-option-label__name')?.textContent?.trim() || '';
      const targetTabName = getTargetTabNameForOption(optionName);
      if (!targetTabName) continue;
      const target = getSeoTargetContainer(targetTabName);
      if (target) return true;
    }
    return false;
  }

  /**
   * Финальные шаги после успеха или после исчерпания попыток (табы и size из Infinite Options должны работать в обоих случаях)
   */
  function finalizeSeparation(successTypes, kingSuccess, nativeSuccess, attempts, failed) {
    separationComplete = true;
    if (separationIntervalId !== null) {
      clearInterval(separationIntervalId);
      separationIntervalId = null;
    }
    scheduleSizeBuildRetry();
    document.dispatchEvent(new CustomEvent('variants:separated', {
      detail: {
        type: (successTypes && successTypes.length) ? successTypes.join(' + ') : (failed ? 'none (gave up)' : 'unknown'),
        kingLinkedOptionsSuccess: !!kingSuccess,
        nativeSuccess: !!nativeSuccess,
        attempts: attempts,
        failed: !!failed,
        timestamp: Date.now()
      }
    }));
    setTimeout(initTabSwitching, 100);
    setTimeout(setupMetalTargetDesign, 150);
  }

  /**
   * Разделяет пикеры King Linked Options по табам
   * Ищет .lv-option-multi-box > .lv-option-box, маппит data-option-name в таргеты, клонирует в #seo-variants-*-target
   * @returns {boolean} - true если хотя бы один пикер перемещён
   */
  function separateKingLinkedOptionsPickers() {
    // В разных версиях приложения контейнер может быть:
    // - king-linked-options-product .lv-option-multi-box (MULTI)
    // - king-linked-options-product (SINGLE) без .lv-option-multi-box
    // - блок с id*="linkedoption"
    const kingRoot = document.querySelector('king-linked-options-product, [id*="linkedoption"] king-linked-options-product');
    const multiBox = document.querySelector('king-linked-options-product .lv-option-multi-box, [id*="linkedoption"] .lv-option-multi-box, .lv-option-multi-box');
    // Ищем боксы по всему king root: часть опций (напр. קראט) может рендериться вне .lv-option-multi-box
    const sourceRoot = kingRoot || multiBox;
    if (!sourceRoot) return false;

    // В некоторых версиях приложения .lv-option-box не является прямым ребёнком .lv-option-multi-box
    // (есть дополнительная обёртка). Поэтому ищем на любом уровне.
    const boxes = sourceRoot.querySelectorAll('.lv-option-box');
    if (!boxes.length) return false;

    let movedCount = 0;

    boxes.forEach((box) => {
      // Не обрабатываем повторно один и тот же оригинальный блок
      if (box.getAttribute('data-seo-separated') === 'true') return;

      const optionName = box.getAttribute('data-option-name') || box.querySelector('.lv-option-label__name')?.textContent?.trim() || '';
      const targetTabName = getTargetTabNameForOption(optionName);
      if (!targetTabName) return;

      const targetContainer = getSeoTargetContainer(targetTabName);
      if (!targetContainer) return;

      // Не дублируем, если уже перенесли эту опцию с тем же названием
      const alreadyMoved = targetContainer.querySelector('.lv-option-box');
      if (alreadyMoved) {
        const existingName = alreadyMoved.getAttribute('data-option-name') || alreadyMoved.querySelector('.lv-option-label__name')?.textContent?.trim();
        if (existingName && existingName.trim() === optionName.trim()) {
          box.setAttribute('data-seo-separated', 'true');
          return;
        }
      }

      const clone = box.cloneNode(true);
      clone.removeAttribute('data-seo-separated');
      targetContainer.appendChild(clone);
      targetContainer.setAttribute('data-option-moved', 'true');
      targetContainer.setAttribute('data-option-name', optionName);
      box.setAttribute('data-seo-separated', 'true');
      movedCount++;
    });

    if (movedCount > 0) {
      const appBlock = document.querySelector('[id*="linkedoption_app_block"]') || kingRoot?.closest('.shopify-app-block') || multiBox?.closest('.shopify-app-block');
      const toHide = appBlock || kingRoot;
      if (toHide) {
        toHide.style.position = 'absolute';
        toHide.style.left = '-9999px';
        toHide.style.width = '1px';
        toHide.style.height = '1px';
        toHide.style.overflow = 'hidden';
        toHide.setAttribute('aria-hidden', 'true');
      }
    }

    return movedCount > 0;
  }

  /**
   * Оформление металл-таргета: карточка, подписи из data-value, активный underline, описание по metal_config
   */
  function setupMetalTargetDesign() {
    const metalTarget = getSeoTargetContainer('metal');
    if (!metalTarget || !metalTarget.querySelector('.lv-option-box')) return;

    if (!metalTarget.querySelector('.metal-options-card')) {
      const card = document.createElement('div');
      card.className = 'metal-options-card';
      while (metalTarget.firstChild) card.appendChild(metalTarget.firstChild);
      metalTarget.appendChild(card);
    }

    const card = metalTarget.querySelector('.metal-options-card');
    const group = card.querySelector('.lv-option-group');
    if (!group) return;

    group.querySelectorAll('.lv-option-wrapper').forEach((wrapper) => {
      const link = wrapper.querySelector('.lv-option-item, a[data-value]');
      if (!link) return;
      const value = link.getAttribute('data-value') || '';
      let labelEl = wrapper.querySelector('.metal-option-label');
      if (!labelEl) {
        labelEl = document.createElement('span');
        labelEl.className = 'metal-option-label';
        labelEl.textContent = value;
      } else {
        labelEl.textContent = value;
      }

      // Делаем label частью ссылки, чтобы весь блок был кликабельным
      if (labelEl.parentElement !== link) {
        labelEl.remove();
        link.appendChild(labelEl);
      }
      wrapper.classList.toggle('has-active', link.classList.contains('lv-option-item__active'));
    });

    let descBlock = card.querySelector('.metal-variant-description');
    if (!descBlock) {
      descBlock = document.createElement('div');
      descBlock.className = 'metal-variant-description';
      card.appendChild(descBlock);
    }

    const activeLink = card.querySelector('.lv-option-item__active, .lv-option-item-link.lv-option-item__active');
    const activeValue = activeLink ? activeLink.getAttribute('data-value') : '';
    let metalConfig = {};
    try {
      const scriptEl = document.getElementById('metal-config-data');
      if (scriptEl && scriptEl.textContent) {
        let raw = JSON.parse(scriptEl.textContent);
        if (typeof raw === 'string') {
          try {
            raw = JSON.parse(raw);
          } catch (e2) {
            raw = {};
          }
        }
        metalConfig = raw && typeof raw === 'object' && !Array.isArray(raw) ? raw : {};
      }
    } catch (e) { /* ignore */ }

    const entry = activeValue && metalConfig[activeValue];
    const flatDescription = metalConfig.description || metalConfig.subtitle;
    const description = (entry && (entry.description || entry.subtitle)) || flatDescription;
    if (description) {
      descBlock.innerHTML = '<p>' + String(description).replace(/</g, '&lt;').replace(/>/g, '&gt;') + '</p>';
      descBlock.style.display = '';
    } else {
      descBlock.innerHTML = '';
      descBlock.style.display = activeValue ? 'none' : '';
    }
  }

  /**
   * Строит кастомный size-picker на основе Infinite Options select
   */
  function buildSizeFromInfiniteOptions() {
    const sizeTarget = getSeoTargetContainer('size');
    if (!sizeTarget || sizeTarget.dataset.infiniteSizeBuilt === 'true') return false;

    // Ищем label с текстом "מידה" или "Size"
    const sizeLabel = document.querySelector('#infiniteoptions-container label[for*="מידה"], #infiniteoptions-container label[for*="size"], #infiniteoptions-container label[for*="Size"]');
    const selectId = sizeLabel ? sizeLabel.getAttribute('for') : null;

    let sizeSelect = selectId ? document.getElementById(selectId) : null;
    if (!sizeSelect) sizeSelect = document.querySelector('#infiniteoptions-container select');
    if (!sizeSelect) {

      return false;
    }

    const options = Array.from(sizeSelect.options || []).filter(opt => opt.value && !opt.disabled);
    if (!options.length) {
      return false;
    }

    sizeTarget.innerHTML = '';
    const wrapper = document.createElement('div');
    wrapper.className = 'option-selector option-selector--with-size-chart';
    const btns = document.createElement('div');
    btns.className = 'option-selector__btns';

    const inputs = [];
    const labels = [];

    const updateState = (value) => {
      inputs.forEach((input, idx) => {
        const label = labels[idx];
        const isActive = input.value === value;
        input.checked = isActive;
        label.classList.toggle('is-selected', isActive);
        label.classList.toggle('active', isActive);
      });
    };

    options.forEach((opt, idx) => {
      const inputId = `infinite-size-${idx}`;
      const input = document.createElement('input');
      input.type = 'radio';
      input.className = 'opt-btn js-option';
      input.name = 'infinite-size-picker';
      input.id = inputId;
      input.value = opt.value;
      input.required = true;
      if (opt.selected) input.checked = true;

      const label = document.createElement('label');
      label.className = 'opt-label';
      label.setAttribute('for', inputId);
      const span = document.createElement('span');
      span.className = 'opt-label__text';
      span.textContent = opt.value;
      label.appendChild(span);

      const handleSelect = () => {
        sizeSelect.value = opt.value;
        sizeSelect.dispatchEvent(new Event('change', { bubbles: true }));
        updateState(opt.value);
      };

      input.addEventListener('change', handleSelect);
      label.addEventListener('click', (e) => {
        e.preventDefault();
        input.checked = true;
        handleSelect();
      });

      btns.appendChild(input);
      btns.appendChild(label);
      inputs.push(input);
      labels.push(label);
    });

    wrapper.appendChild(btns);
    sizeTarget.appendChild(wrapper);

    // синхронизация если select меняется извне
    sizeSelect.addEventListener('change', () => updateState(sizeSelect.value));

    const initialValue = sizeSelect.value || options[0].value;
    sizeSelect.value = initialValue;
    updateState(initialValue);

    // Скрываем оригинальный infiniteoptions контейнер
    const ioContainer = document.querySelector('#infiniteoptions-container');
    if (ioContainer) {
      ioContainer.style.position = 'absolute';
      ioContainer.style.left = '-9999px';
      ioContainer.style.width = '1px';
      ioContainer.style.height = '1px';
      ioContainer.style.overflow = 'hidden';
      ioContainer.setAttribute('aria-hidden', 'true');
    }

    sizeTarget.dataset.infiniteSizeBuilt = 'true';
    return true;
  }

  // Ретраи для сборки размера
  function scheduleSizeBuildRetry(maxAttempts = 20, interval = 400) {
    if (buildSizeFromInfiniteOptions()) {
      hideEmptyTabsAndActivateFirst();
      return;
    }
    if (sizeBuildAttempts >= maxAttempts) {
      return;
    } 
    sizeBuildAttempts++;
    setTimeout(() => scheduleSizeBuildRetry(maxAttempts, interval), interval);
  }
  
  /**
   * Определяет тип вариантов на странице
   * @returns {string} - 'king-linked-options' | 'native' | 'none'
   */
  function detectVariantsType() {
    // King Linked Options: блок приложения или данные в разметке
    const kingContainer = document.querySelector('king-linked-options-product .lv-option-multi-box, [id*="linkedoption"] .lv-option-multi-box');
    const kingData = document.getElementById('lv-option-product-group-data');
    if ((kingContainer && kingContainer.querySelectorAll('.lv-option-box').length > 0) || (window.kingLinkedOptionConfig && window.kingLinkedOptionConfig.product && window.kingLinkedOptionConfig.product.active)) {
      return 'king-linked-options';
    }
    if (kingData && document.querySelector('.lv-option-box')) {
      return 'king-linked-options';
    }

    // Нативные Shopify варианты
    const nativeVariants = document.querySelector('.option-selectors .option-selector');
    if (nativeVariants) {
      return 'native';
    }

    return 'none';
  }

  /**
   * Текст названия опции: listed — legend.label; dropdown (custom-select) — label.label
   */
  function getNativeOptionName(selector) {
    const legend = selector.querySelector('legend.label');
    if (legend) {
      return (
        legend.childNodes[0]?.textContent?.trim() ||
        legend.textContent.replace(legend.querySelector('.label__value')?.textContent || '', '').trim()
      );
    }
    const dropLabel = selector.querySelector('label.label');
    if (dropLabel) {
      return dropLabel.textContent.trim();
    }
    return '';
  }

  /**
   * Синхронизация клона custom-select (dropdown) с оригиналом; инициализация cc-select на клоне
   */
  function setupNativeDropdownSync(clone, original) {
    const origCc = original.querySelector('.cc-select');
    const cloneCc = clone.querySelector('.cc-select');
    if (!origCc || !cloneCc) {
      return;
    }

    const suffix = '-seo-tab-' + Date.now() + '-' + Math.random().toString(36).slice(2, 9);
    const idMap = new Map();
    clone.querySelectorAll('[id]').forEach((el) => {
      const oldId = el.id;
      const newId = oldId + suffix;
      idMap.set(oldId, newId);
      el.id = newId;
    });
    clone.querySelectorAll('[for]').forEach((el) => {
      const f = el.getAttribute('for');
      if (f && idMap.has(f)) {
        el.setAttribute('for', idMap.get(f));
      }
    });
    ['aria-labelledby', 'aria-activedescendant'].forEach((attr) => {
      clone.querySelectorAll('[' + attr + ']').forEach((el) => {
        const v = el.getAttribute(attr);
        if (!v) return;
        const next = v
          .split(/\s+/)
          .map((id) => idMap.get(id) || id)
          .join(' ');
        el.setAttribute(attr, next);
      });
    });

    // cloneNode копирует cc-initialized с оригинала — иначе cc:component:load пропустит инициализацию
    cloneCc.classList.remove('cc-initialized');

    if (window.jQuery) {
      window.jQuery(document).trigger('cc:component:load', ['custom-select', clone]);
    }

    function selectedValueInCc(cc) {
      const sel = cc.querySelector('.cc-select__option[aria-selected="true"]');
      return sel ? String(sel.dataset.value || '') : '';
    }

    function pickOptionInCc(cc, value) {
      const want = value == null ? '' : String(value);
      const opt = Array.from(cc.querySelectorAll('.cc-select__option.js-option')).find(
        (li) => String(li.dataset.value || '') === want
      );
      if (opt) {
        opt.dispatchEvent(new MouseEvent('click', { bubbles: true }));
      }
    }

    let syncing = false;

    cloneCc.addEventListener('change', function (e) {
      if (syncing) return;
      const detail = e.detail;
      const val =
        detail && typeof detail.selectedValue !== 'undefined'
          ? detail.selectedValue
          : selectedValueInCc(cloneCc);
      if (String(selectedValueInCc(origCc)) === String(val == null ? '' : val)) {
        return;
      }
      syncing = true;
      try {
        pickOptionInCc(origCc, val);
      } finally {
        syncing = false;
      }
    });

    origCc.addEventListener('change', function (e) {
      if (syncing) return;
      const detail = e.detail;
      if (!detail || typeof detail.selectedValue === 'undefined') return;
      const val = detail.selectedValue;
      if (String(selectedValueInCc(cloneCc)) === String(val == null ? '' : val)) {
        return;
      }
      syncing = true;
      try {
        pickOptionInCc(cloneCc, val);
      } finally {
        syncing = false;
      }
    });
  }

  /**
   * Функция для поиска и перемещения нативных Shopify вариантов
   * Формат: .option-selectors > .option-selector (listed: legend.label; dropdown: label.label + .cc-select)
   * @returns {boolean} - true если пикеры успешно перемещены
   */
  function separateNativeVariantsPickers() {
    // Шаг 1: Находим контейнер с нативными вариантами (только основная PDP, не quickbuy)
    const mainForm = getMainProductForm();
    const optionSelectors = mainForm ? mainForm.querySelector('.option-selectors') : document.querySelector('.option-selectors');
    
    if (!optionSelectors) {
      return false;
    }
    
    // Шаг 2: Находим все селекторы опций
    const allOptionSelectors = optionSelectors.querySelectorAll('.option-selector');
    
    if (allOptionSelectors.length === 0) {
      return false;
    }
    

    
    let movedCount = 0;
    
    // Шаг 3: Обрабатываем каждый селектор
    allOptionSelectors.forEach((selector, index) => {
      // Пропускаем уже обработанные селекторы
      if (selector.getAttribute('data-hidden-original') === 'true') {
        return;
      }
      
      const optionName = getNativeOptionName(selector);
      if (!optionName) {
        return;
      }
      
      const optionNameLower = optionName.toLowerCase();
      
      
      // Определяем целевую вкладку на основе названия опции
      // Поддержка английских и ивритских названий + новые: color/clarity
      let targetTabName = null;
      
      // Metal / מתכת
      if (optionNameLower.includes('metal') || optionName.includes('מתכת')) {
        targetTabName = 'metal';
      } 
      // Carat / קראט
      else if (optionNameLower.includes('carat') || optionName.includes('קראט')) {
        targetTabName = 'main-carat';
      } 
      // Diamond Type / סוג יהלומים (не матчим общий "סוג", чтобы не ловить "סוגרים")
      else if (optionNameLower.includes('type') || optionName.includes('יהלום') || optionName.includes('יהלומים') || optionName.includes('סוג יהלום') || optionName.includes('סוג היהלום')) {
        targetTabName = 'diamond-type';
      } 
      // Size / מידה
      else if (optionNameLower.includes('size') || optionName.includes('מידה')) {
        targetTabName = 'size';
      }
      // Backings / גיבויים / סוגרים
      else if (optionNameLower.includes('backing') || optionNameLower.includes('backings') || optionName.includes('גיבויים') || optionName.includes('גיבוי') || optionName.includes('סוגרים')) {
        targetTabName = 'backings';
      }
      // Color / צבע
      else if (optionNameLower.includes('color') || optionName.includes('צבע')) {
        targetTabName = 'color';
      }
      // Clarity / בהירות / ניקיון
      else if (optionNameLower.includes('clarity') || optionName.includes('בהירות') || optionName.includes('ניקיון')) {
        targetTabName = 'clarity';
      }
      // Purity / טוהר
      else if (optionNameLower.includes('purity') || optionName.includes('טוהר')) {
        targetTabName = 'purity';
      }
      
      if (!targetTabName) {
        return;
      }
      
      // Генерируем селектор целевого контейнера
      const targetContainer = getSeoTargetContainer(targetTabName);
      
      if (!targetContainer) {
        console.warn('[Variants Separator] ✗ Target not found: seo-variants-' + targetTabName + '-target');
        return;
      }
      
      // Клонируем весь селектор (чтобы сохранить обработчики на оригинале)
      const selectorClone = selector.cloneNode(true);
      
      // Добавляем атрибуты для связи
      selectorClone.setAttribute('data-cloned-from', `option-${index}`);
      selector.setAttribute('data-original-index', index);
      
      // Скрываем оригинал
      selector.style.display = 'none';
      selector.setAttribute('data-hidden-original', 'true');
      
      // Добавляем клон в целевой контейнер
      targetContainer.appendChild(selectorClone);
      
      // Настраиваем синхронизацию между клоном и оригиналом (listed = radio; dropdown = custom-select)
      const isDropdown =
        selector.getAttribute('data-selector-type') === 'dropdown' ||
        (!!selector.querySelector('.cc-select') &&
          !selector.querySelector('fieldset input.js-option[type="radio"]'));
      if (isDropdown && selector.querySelector('.cc-select')) {
        setupNativeDropdownSync(selectorClone, selector);
      } else {
        setupNativeVariantSync(selectorClone, selector);
      }
      
      // Сохраняем метаданные
      targetContainer.setAttribute('data-option-moved', 'true');
      targetContainer.setAttribute('data-option-name', optionName);
      
      movedCount++;
    });
    
    if (movedCount > 0) {
      
      // Активируем первую вкладку с контентом
      hideEmptyTabsAndActivateFirst();
    }
    
    return movedCount > 0;
  }

  /**
   * Настраивает синхронизацию между клонированным и оригинальным селектором
   * @param {Element} clone - Клонированный селектор
   * @param {Element} original - Оригинальный селектор
   */
  function setupNativeVariantSync(clone, original) {
    // Находим все radio inputs в клоне и оригинале
    const cloneInputs = clone.querySelectorAll('input.js-option');
    const originalInputs = original.querySelectorAll('input.js-option');
    const cloneLabels = clone.querySelectorAll('label.opt-label');
    
    // Генерируем уникальный префикс для клонированных ID
    const uniquePrefix = `clone-${Date.now()}-`;
    
    // Обновляем ID инпутов и for атрибуты лейблов в клоне
    cloneInputs.forEach((input, idx) => {
      const oldId = input.id;
      const newId = uniquePrefix + oldId;
      input.id = newId;
      
      // Находим соответствующий label и обновляем его for
      const label = clone.querySelector(`label[for="${oldId}"]`);
      if (label) {
        label.setAttribute('for', newId);
      }
    });
    
    // Обновляем ссылки на лейблы после смены ID
    const updatedCloneLabels = clone.querySelectorAll('label.opt-label');
    
    /**
     * Обновляет визуальное состояние выбранного варианта
     */
    function updateVisualState(selectedInput) {
      // Убираем активный класс со всех labels в клоне
      updatedCloneLabels.forEach(label => {
        label.classList.remove('is-selected', 'active');
      });
      
      // Добавляем активный класс выбранному label
      const selectedLabel = clone.querySelector(`label[for="${selectedInput.id}"]`);
      if (selectedLabel) {
        selectedLabel.classList.add('is-selected', 'active');
      }
      
      // Обновляем отображение текущего значения в legend (если есть)
      const labelValue = clone.querySelector('.label__value');
      if (labelValue) {
        labelValue.textContent = selectedInput.value;
      }
    }
    
    cloneInputs.forEach((cloneInput, index) => {
      const originalInput = originalInputs[index];
      if (!originalInput) return;
      
      // Устанавливаем начальное визуальное состояние для checked элемента
      if (cloneInput.checked) {
        updateVisualState(cloneInput);
      }
      
      // При клике на клон - триггерим клик на оригинале + обновляем визуал
      cloneInput.addEventListener('change', function(e) {
        // console.log(`[Variants Separator] 🔄 Syncing: ${cloneInput.value}`);
        
        // Обновляем визуальное состояние
        updateVisualState(cloneInput);
        
        // Устанавливаем значение и триггерим change на оригинале
        originalInput.checked = true;
        originalInput.dispatchEvent(new Event('change', { bubbles: true }));
        
        // Также триггерим click для совместимости
        originalInput.click();
      });
      
      // Также обрабатываем клики на label
      const cloneLabel = clone.querySelector(`label[for="${cloneInput.id}"]`);
      if (cloneLabel) {
        cloneLabel.addEventListener('click', function(e) {
          e.preventDefault();
          cloneInput.checked = true;
          
          // Обновляем визуальное состояние
          updateVisualState(cloneInput);
          
          cloneInput.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }
    });

  }

  /**
   * Скрывает пустые вкладки и активирует первую с контентом
   * ОПТИМИЗАЦИЯ: Учитывает контент из метафилдов (info-блоки), 
   * чтобы показывать вкладки сразу без ожидания асинхронных данных
   */
  function hideEmptyTabsAndActivateFirst() {
    const tabsContainer = getMainProductOptionsTabsRoot();
    if (!tabsContainer) return;
    
    // Поддержка нового дизайна (tab-select) и старого (tab-btn)
    const tabButtons = tabsContainer.querySelectorAll('.tab-select, .tab-btn');
    const tabContents = tabsContainer.querySelectorAll('.tab-content');
    
    let firstTabWithContent = null;
    
    // Проходим по всем вкладкам
    tabButtons.forEach(btn => {
      const tabName = btn.dataset.tab;
      let content = tabsContainer.querySelector(`[data-tab-content="${tabName}"]`);
      if (!content && (tabName === 'her-size' || tabName === 'his-size')) {
        content = tabsContainer.querySelector('[data-tab-content="size"]');
      }
      
      if (!content) {
        btn.style.display = 'none';
        return;
      }
      
      // === ОПТИМИЗАЦИЯ: Проверяем несколько источников контента ===
      let hasContent = false;
      
      // 1. Проверяем target-контейнер (для SEO вариантов / нативных опций)
      const targetContainer = content.querySelector(`[id$="-target"]`);
      if (targetContainer && targetContainer.children.length > 0) {
        hasContent = true;
      }
      
      // 2. Проверяем info-блоки из метафилдов (рендерятся серверно)
      // ВАЖНО: не считаем carat-tab-grid контентом само по себе — эта обёртка есть всегда,
      // даже когда внутри нет вариантов, и из-за этого вкладка "Carat" ошибочно показывается.
      if (!hasContent) {
        const infoBlock = content.querySelector('.variant-info-block, .size-info-block, .size-tab-container');
        if (infoBlock) {
          hasContent = true;
        }
      }

      // Carat: считаем контентом только реальные варианты в таргете
      if (!hasContent && tabName === 'main-carat') {
        const caratTarget = content.querySelector('#seo-variants-main-carat-target');
        if (caratTarget && caratTarget.children.length > 0) {
          hasContent = true;
        }
      }
      
      // 3. Size: проверяем наличие кастомного select или Infinite Options
      if (!hasContent && tabName === 'size') {
        const customSelect = content.querySelector('#size-custom-select');
        const infiniteSelect = document.querySelector('#infiniteoptions-container select');
        if (customSelect || infiniteSelect) {
          hasContent = true;
        }
      }
      if (!hasContent && (tabName === 'her-size' || tabName === 'his-size')) {
        const hasWeddingSelect = content.querySelector('#her-size-custom-select, #his-size-custom-select');
        if (hasWeddingSelect) {
          hasContent = true;
        }
      }
      
      if (hasContent) {
        // Показываем кнопку вкладки
        btn.style.display = '';
        btn.classList.remove('tab-select--hidden');
        btn.removeAttribute('data-empty-tab');
        
        // Запоминаем первую вкладку с контентом
        if (!firstTabWithContent) {
          firstTabWithContent = { btn, content, tabName };
        }
      } else {
        // Скрываем пустую вкладку
        btn.style.display = 'none';
        btn.classList.add('tab-select--hidden');
        btn.setAttribute('data-empty-tab', 'true');
        btn.classList.remove('active');
        content.classList.remove('active');
      }
    });
    
    // 🔒 ИЗМЕНЕНО: Оставляем все табы закрытыми по умолчанию
    // Пользователь откроет нужный таб кликом
    // Убираем active со всех табов и контента
    tabButtons.forEach(b => b.classList.remove('active'));
    tabContents.forEach(c => c.classList.remove('active'));
    
    // Комментируем старый код автоактивации:
    // if (firstTabWithContent) {
    //   // Сначала убираем active со всех
    //   tabButtons.forEach(b => b.classList.remove('active'));
    //   tabContents.forEach(c => c.classList.remove('active'));
    //   
    //   // Проверяем есть ли Size вкладка с контентом
    //   const sizeBtn = tabsContainer.querySelector('.tab-select[data-tab="size"], .tab-btn[data-tab="size"]');
    //   const sizeContent = tabsContainer.querySelector('[data-tab-content="size"]');
    //   
    //   if (sizeBtn && sizeContent && sizeBtn.getAttribute('data-empty-tab') !== 'true') {
    //     // Активируем Size как дефолтную
    //     sizeBtn.classList.add('active');
    //     sizeContent.classList.add('active');
    //   } else {
    //     // Активируем первую с контентом
    //     firstTabWithContent.btn.classList.add('active');
    //     firstTabWithContent.content.classList.add('active');
    //   }
    // }
  }

  /**
   * Инициализация с retry логикой
   */
  function initSEOVariantsSeparation() {
    if (separationComplete) {
      return;
    }
    // Не запускать второй setInterval поверх уже идущего (типичная причина "вечной" загрузки)
    if (separationIntervalId !== null) {
      return;
    }

    injectStyles();

    let attempts = 0;
    const maxAttempts = 20;
    const retryInterval = 400;

    separationIntervalId = setInterval(() => {
      try {
        if (separationComplete) {
          clearInterval(separationIntervalId);
          separationIntervalId = null;
          return;
        }

        attempts++;

        const variantsType = detectVariantsType();
        let kingSuccess = false;
        let nativeSuccess = false;

        if (variantsType === 'king-linked-options') {
          kingSuccess = separateKingLinkedOptionsPickers();
        }
        nativeSuccess = separateNativeVariantsPickers();

        const success = kingSuccess || nativeSuccess;
        const successTypes = [];
        if (kingSuccess) successTypes.push('king-linked-options');
        if (nativeSuccess) successTypes.push('native');

        if (success) {
          clearInterval(separationIntervalId);
          separationIntervalId = null;
          finalizeSeparation(successTypes, kingSuccess, nativeSuccess, attempts, false);
        } else if (attempts >= maxAttempts) {
          clearInterval(separationIntervalId);
          separationIntervalId = null;
          console.warn('[Variants Separator] Gave up after ' + maxAttempts + ' attempts (type: ' + variantsType + '). Tabs will still init.');
          finalizeSeparation([], false, false, attempts, true);
        }
      } catch (err) {
        console.error('[Variants Separator] Separation error:', err);
        clearInterval(separationIntervalId);
        separationIntervalId = null;
        finalizeSeparation([], false, false, attempts, true);
      }
    }, retryInterval);
  }

  /**
   * Инициализация переключения табов
   */
  function initTabSwitching() {
    const tabsContainer = getMainProductOptionsTabsRoot();
    
    if (!tabsContainer) {
      // console.log('[SEOVariants Separator] ⚠️  Tabs container not found');
      return;
    }

    // Защита от повторной инициализации (иначе клики отрабатывают 2+ раза: открыть → сразу закрыть)
    if (tabsContainer.getAttribute('data-tab-switching-initialized') === 'true') {
      hideEmptyTabsAndActivateFirst();
      return;
    }
    tabsContainer.setAttribute('data-tab-switching-initialized', 'true');

    // Поддержка нового дизайна (tab-select) и старого (tab-btn)
    const tabButtons = tabsContainer.querySelectorAll('.tab-select, .tab-btn');
    const tabContents = tabsContainer.querySelectorAll('.tab-content');

    if (tabButtons.length === 0) {
      // console.log('[SEOVariants Separator] ⚠️  No tab buttons found');
      return;
    }

    tabButtons.forEach(button => {
      button.addEventListener('click', function() {
        // Не переключаем на скрытые вкладки
        if (this.getAttribute('data-empty-tab') === 'true') {
          return;
        }
        
        const targetTab = this.dataset.tab;
        const isCurrentlyActive = this.classList.contains('active');
        let targetContent = tabsContainer.querySelector(`[data-tab-content="${targetTab}"]`);
        const weddingSizeTab = targetTab === 'her-size' || targetTab === 'his-size';
        if (!targetContent && weddingSizeTab) {
          targetContent = tabsContainer.querySelector('[data-tab-content="size"]');
        }
        
        // Если кликнули по уже активной - закрываем (toggle)
        if (isCurrentlyActive) {
          // Добавляем класс closing для анимации
          if (targetContent) {
            targetContent.classList.add('closing');
          }
          
          // Убираем active после задержки
          setTimeout(() => {
            this.classList.remove('active');
            if (targetContent) {
              targetContent.classList.remove('active', 'closing');
              if (weddingSizeTab) {
                targetContent.removeAttribute('data-active-wedding-panel');
              }
            }
          }, 250);
          return;
        }
        
        // Переключение на другой таб
        const currentActiveContent = tabsContainer.querySelector('.tab-content.active');
        
        // Убираем активность со всех кнопок
        tabButtons.forEach(btn => btn.classList.remove('active'));
        
        // Активируем выбранную кнопку
        this.classList.add('active');
        
        if (currentActiveContent && currentActiveContent !== targetContent) {
          // Сначала закрываем старый контент
          currentActiveContent.classList.add('closing');
          
          // Ждём завершения анимации закрытия, потом открываем новый
          setTimeout(() => {
            currentActiveContent.classList.remove('active', 'closing');
            
            // Теперь открываем новый контент
            if (targetContent) {
              targetContent.classList.remove('closing');
              targetContent.classList.add('active');
              if (weddingSizeTab) {
                targetContent.setAttribute('data-active-wedding-panel', targetTab);
              }
            }
          }, 250);
        } else {
          // Если не было активного контента - открываем сразу
          if (targetContent) {
            targetContent.classList.remove('closing');
            targetContent.classList.add('active');
            if (weddingSizeTab) {
              targetContent.setAttribute('data-active-wedding-panel', targetTab);
            }
          }
        }
      });
    });

    // console.log(`[Variants Separator] ✅ Tab switching initialized (${tabButtons.length} tabs)`);
    
    // Скрываем пустые вкладки и активируем первую с контентом
    hideEmptyTabsAndActivateFirst();
  }

  // ============================================
  // ИНИЦИАЛИЗАЦИЯ
  // ============================================

  // Запуск при загрузке DOM
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function() {
      // console.log('[SEOVariants Separator] DOMContentLoaded event');
      setTimeout(initSEOVariantsSeparation, 200);
    });
  } else {
    // console.log('[SEOVariants Separator] DOM already loaded');
    setTimeout(initSEOVariantsSeparation, 200);
  }

  // Дополнительная попытка после полной загрузки
  window.addEventListener('load', function() {
    // console.log('[SEOVariants Separator] Window load event');
    setTimeout(initSEOVariantsSeparation, 500);
  });

  // MutationObserver для позднего King: только после load, только дёшевые проверки (без querySelector по всему поддереву).
  if (window.MutationObserver) {
    function scheduleSeparationFromObserver() {
      if (observerDebounceTimer) {
        clearTimeout(observerDebounceTimer);
      }
      observerDebounceTimer = setTimeout(function() {
        observerDebounceTimer = null;
        if (separationComplete && !needsLateKingSeparation()) {
          return;
        }
        if (separationComplete && needsLateKingSeparation()) {
          if (lateSeparationObserverRetries >= MAX_LATE_SEPARATION_OBSERVER_RETRIES) {
            return;
          }
          lateSeparationObserverRetries = 1 + lateSeparationObserverRetries;
          separationComplete = false;
        }
        initSEOVariantsSeparation();
      }, 500);
    }

    const observer = new MutationObserver(function(mutations) {
      for (let mi = 0; mi < mutations.length; mi++) {
        const mutation = mutations[mi];
        if (!mutation.addedNodes.length) continue;
        for (let i = 0; i < mutation.addedNodes.length; i++) {
          const node = mutation.addedNodes[i];
          if (node.nodeType !== 1) continue;
          const nodeId = (node.getAttribute && node.getAttribute('id')) || (typeof node.id === 'string' ? node.id : '');
          // НЕ вызывать node.querySelector по всему поддереву — это убивало главный поток при гидратации.
          // King часто приходит внутри app block: дешёвая подсказка без полного обхода DOM.
          if (node.tagName === 'KING-LINKED-OPTIONS-PRODUCT' ||
              (nodeId && nodeId.indexOf('linkedoption') !== -1) ||
              (nodeId && nodeId.indexOf('shopify-block') !== -1)) {
            scheduleSeparationFromObserver();
            return;
          }
        }
      }
    });

    function attachBodyObserver() {
      if (!document.body) return;
      observer.observe(document.body, {
        childList: true,
        subtree: true
      });
    }

    if (document.readyState === 'complete') {
      setTimeout(attachBodyObserver, 100);
    } else {
      window.addEventListener('load', function() {
        setTimeout(attachBodyObserver, 100);
      });
    }
  }

})();
